/**
 * Material 3 Dynamic Color Context for Bonapisca
 * Implements Material 3 Expressive dynamic color system with Mediterranean presets
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  Material3ColorScheme,
  MediterraneanColorContext,
  ColorContextType,
  MediterraneanPreset,
} from '../types/material3';

// Material 3 Expressive Mediterranean Color Presets - Vibrant & Expressive
const MEDITERRANEAN_PRESETS = {
  classic: {
    primary: '#0077b6',
    secondary: '#00b4d8',
    tertiary: '#90e0ef',
    name: 'Atlantic Current',
    description: 'theme.presetDescriptions.classic',
  },
  dawn: {
    primary: '#00b4d8',
    secondary: '#90e0ef',
    tertiary: '#caf0f8',
    name: 'Morning Tide',
    description: 'theme.presetDescriptions.dawn',
  },
  coral: {
    primary: '#0077b6',
    secondary: '#90e0ef',
    tertiary: '#caf0f8',
    name: 'Harbor Light',
    description: 'theme.presetDescriptions.coral',
  },
  depth: {
    primary: '#03045e',
    secondary: '#0077b6',
    tertiary: '#00b4d8',
    name: 'Deep Twilight',
    description: 'theme.presetDescriptions.depth',
  },
  sunset: {
    primary: '#0077b6',
    secondary: '#00b4d8',
    tertiary: '#caf0f8',
    name: 'Open Water',
    description: 'theme.presetDescriptions.sunset',
  },
} as const;

// Enhanced Material 3 Expressive color scheme - more vibrant and expressive
const createDefaultColorScheme = (preset: MediterraneanPreset = 'classic'): Material3ColorScheme => {
  const colors = MEDITERRANEAN_PRESETS[preset];

  return {
    primary: colors.primary,
    onPrimary: '#ffffff',
    primaryContainer: '#caf0f8',
    onPrimaryContainer: '#03045e',

    secondary: colors.secondary,
    onSecondary: '#03045e',
    secondaryContainer: '#90e0ef',
    onSecondaryContainer: '#03045e',

    tertiary: colors.tertiary,
    onTertiary: '#03045e',
    tertiaryContainer: '#caf0f8',
    onTertiaryContainer: '#03045e',

    error: '#B42318',
    onError: '#ffffff',
    errorContainer: '#FEF3F2',
    onErrorContainer: '#7A271A',

    surface: '#ffffff',
    onSurface: '#03045e',
    surfaceVariant: '#eefbfe',
    onSurfaceVariant: '#0077b6',

    background: '#f8feff',
    onBackground: '#03045e',

    outline: '#90e0ef',
    outlineVariant: '#caf0f8',
  };
};

const createMediterraneanContext = (preset: MediterraneanPreset = 'classic'): MediterraneanColorContext => {
  const colors = MEDITERRANEAN_PRESETS[preset];
  
  return {
    bluePrimary: colors.primary,
    greenSuccess: colors.secondary,
    goldAccent: colors.tertiary,
    coralWarning: MEDITERRANEAN_PRESETS.coral.secondary,
  };
};

// Create the context
const ColorContext = createContext<ColorContextType | null>(null);

// Storage keys
const STORAGE_KEYS = {
  PRIMARY_COLOR: 'mediterranean-primary-color',
  COLOR_SCHEME: 'mediterranean-color-scheme',
  PRESET: 'mediterranean-preset',
} as const;

export interface ColorProviderProps {
  children: React.ReactNode;
  defaultPreset?: MediterraneanPreset;
  enableSystemTheme?: boolean;
}

export const ColorProvider: React.FC<ColorProviderProps> = ({
  children,
  defaultPreset = 'classic',
  enableSystemTheme = true,
}) => {
  // Load saved preferences or use defaults
  const [currentPreset, setCurrentPreset] = useState<MediterraneanPreset>(() => {
    if (typeof window === 'undefined') return defaultPreset;
    
    const savedPreset = localStorage.getItem(STORAGE_KEYS.PRESET) as MediterraneanPreset;
    return savedPreset && savedPreset in MEDITERRANEAN_PRESETS ? savedPreset : defaultPreset;
  });

  const [primaryColor, setPrimaryColor] = useState<string>(() => {
    if (typeof window === 'undefined') return MEDITERRANEAN_PRESETS[defaultPreset].primary;
    
    return localStorage.getItem(STORAGE_KEYS.PRIMARY_COLOR) || MEDITERRANEAN_PRESETS[currentPreset].primary;
  });

  const [colorScheme, setColorScheme] = useState<'light' | 'dark' | 'auto'>(() => {
    if (typeof window === 'undefined') return 'auto';
    
    return (localStorage.getItem(STORAGE_KEYS.COLOR_SCHEME) as 'light' | 'dark' | 'auto') || 'auto';
  });

  const [dynamicColors, setDynamicColors] = useState<Material3ColorScheme>(() => 
    createDefaultColorScheme(currentPreset)
  );

  const [mediterraneanColors, setMediterraneanColors] = useState<MediterraneanColorContext>(() =>
    createMediterraneanContext(currentPreset)
  );

  // Keep the visible app on the fixed five-color palette only.
  const generatePalette = useCallback((sourceColor: string, preset?: MediterraneanPreset) => {
    const resolvedPreset = preset ?? currentPreset;
    const presetColors = MEDITERRANEAN_PRESETS[resolvedPreset];
    const newColorScheme = createDefaultColorScheme(resolvedPreset);
    const newMediterraneanColors = createMediterraneanContext(resolvedPreset);

    setDynamicColors(newColorScheme);
    setMediterraneanColors(newMediterraneanColors);
    setPrimaryColor(presetColors.primary);

    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.PRIMARY_COLOR, presetColors.primary);
      localStorage.setItem(STORAGE_KEYS.PRESET, resolvedPreset);
      setCurrentPreset(resolvedPreset);
    }

    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      Object.entries(newColorScheme).forEach(([key, value]) => {
        const cssVar = `--md-sys-color-${key.replace(/[A-Z]/g, '-$&').toLowerCase()}`;
        root.style.setProperty(cssVar, value);
      });

      root.style.setProperty('--mediterranean-blue-primary', newMediterraneanColors.bluePrimary);
      root.style.setProperty('--mediterranean-green-success', newMediterraneanColors.greenSuccess);
      root.style.setProperty('--mediterranean-gold-accent', newMediterraneanColors.goldAccent);
      root.style.setProperty('--mediterranean-coral-warning', newMediterraneanColors.coralWarning);
    }
  }, [currentPreset]);

  // Handle system theme changes
  useEffect(() => {
    if (!enableSystemTheme || colorScheme !== 'auto') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      // Regenerate palette when system theme changes
      generatePalette(primaryColor, currentPreset);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [enableSystemTheme, colorScheme, primaryColor, generatePalette]);

  // Apply preset
  const applyPreset = useCallback((preset: MediterraneanPreset) => {
    const presetColors = MEDITERRANEAN_PRESETS[preset];
    generatePalette(presetColors.primary, preset);
  }, [generatePalette]);

  // Initialize color system on mount
  useEffect(() => {
    generatePalette(primaryColor, currentPreset);
  }, []); // Run once on mount

  // Context value
  const handleColorSchemeChange = useCallback((scheme: 'light' | 'dark' | 'auto') => {
    setColorScheme(scheme);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.COLOR_SCHEME, scheme);
    }
    // Regenerate palette with new scheme
    generatePalette(primaryColor, currentPreset);
  }, [generatePalette, primaryColor, currentPreset]);

  const contextValue: ColorContextType = {
    primaryColor,
    colorScheme,
    currentPreset,
    availablePresets: MEDITERRANEAN_PRESETS,
    generatePalette,
    dynamicColors,
    mediterraneanColors,
    setPreset: applyPreset,
    setColorScheme: handleColorSchemeChange,
    applyPreset,
  };

  return (
    <ColorContext.Provider value={contextValue}>
      {children}
    </ColorContext.Provider>
  );
};

// Custom hook to use colors
export const useColors = (): ColorContextType => {
  const context = useContext(ColorContext);
  
  if (!context) {
    throw new Error('useColors must be used within a ColorProvider');
  }
  
  return context;
};

// Export preset types and constants
export { MEDITERRANEAN_PRESETS };
